import React, { useState, useEffect, useCallback } from 'react';
import secureStorage from '../../lib/secureStorage';
import authedFetch from '../../lib/authedFetch';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChart3, Loader2, TrendingUp, TrendingDown, ArrowRight, Sparkles, RefreshCw, ArrowUpRight, ArrowDownRight, CheckCircle2, AlertTriangle, History, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScoreTrendsView({ organizationId, isDark, navigate }) {
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const [kpiInfluence, setKpiInfluence] = useState([]);
  const [insight, setInsight] = useState(null);      // {insight, generated_at, cached, available}
  const [genLoading, setGenLoading] = useState(false);
  const [drillKpi, setDrillKpi] = useState(null);     // KPI row for momentum drill-in
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const authHeaders = useCallback(() => {
    const token = secureStorage.getItem('session_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (organizationId && organizationId !== 'all') p.append('organization_id', organizationId);
    return p;
  }, [organizationId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const h = authHeaders();
      const p = params();
      const [hlRes, infRes] = await Promise.all([
        authedFetch(`${API_URL}/api/lei-health/overview?${p}`, h),
        authedFetch(`${API_URL}/api/lei-health/kpi-influence?${p}`, h),
      ]);
      const hl = hlRes.ok ? await hlRes.json() : null;
      const inf = infRes.ok ? await infRes.json() : null;
      // Aligned time window: same canonical trend_data (6mo) the full Score Trends
      // page uses — latest point == the Overview "Current LEI".
      const pts = (hl?.trend_data || []).map((t) => ({ month: t.month, lei: Math.round(t.score * 10) / 10 }));
      setSeries(pts);
      setKpiInfluence(inf?.kpis || []);
      setAlerts(inf?.alerts || []);
      // Pull any cached AI summary (never auto-spends).
      const insRes = await authedFetch(`${API_URL}/api/lei-health/influence-insight`, {
        method: 'POST', headers: { ...h.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: organizationId, cached_only: true }),
      });
      const ins = insRes.ok ? await insRes.json() : null;
      setInsight(ins?.available ? ins : null);
    } catch (e) { setSeries([]); } finally { setLoading(false); }
  }, [authHeaders, params, organizationId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const generateInsight = useCallback(async (force) => {
    setGenLoading(true);
    try {
      const h = authHeaders();
      const res = await authedFetch(`${API_URL}/api/lei-health/influence-insight`, {
        method: 'POST', headers: { ...h.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: organizationId, force: !!force }),
      });
      if (res.ok) { const d = await res.json(); if (d?.available) setInsight(d); }
    } finally { setGenLoading(false); }
  }, [authHeaders, organizationId]);

  const toggleHistory = useCallback(async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history == null) {
      try {
        const p = params();
        const res = await authedFetch(`${API_URL}/api/lei-health/influence-insight/history?${p}`, authHeaders());
        const d = res.ok ? await res.json() : null;
        setHistory(d?.history || []);
      } catch { setHistory([]); }
    }
  }, [showHistory, history, params, authHeaders]);

  const card = cn('rounded-2xl border p-5', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200');
  if (loading) return <div className="flex justify-center py-20" data-testid="score-trends-loading"><Loader2 className={cn('h-6 w-6 animate-spin', isDark ? 'text-slate-400' : 'text-slate-500')} /></div>;
  if (series.length < 2) return <div className={cn('text-center py-16 rounded-2xl border', isDark ? 'bg-[#0d1526] border-[#22304d] text-slate-400' : 'bg-white border-slate-200 text-slate-500')} data-testid="score-trends-empty"><BarChart3 className="h-8 w-8 mx-auto mb-3 opacity-50" /><p className="text-sm">Not enough history to chart trends yet.</p></div>;

  const ys = series.map((p) => p.lei);
  const half = Math.floor(series.length / 2);
  const avg = (arr) => arr.reduce((a, b) => a + b, 0) / (arr.length || 1);
  const firstH = Math.round(avg(ys.slice(0, half)) * 10) / 10;
  const secondH = Math.round(avg(ys.slice(half)) * 10) / 10;
  const delta = Math.round((secondH - firstH) * 10) / 10;
  const up = delta >= 0;

  return (
    <div className="space-y-4" data-testid="score-trends-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: 'Current LEI', v: ys[ys.length - 1] },
          { l: 'First half avg', v: firstH },
          { l: 'Second half avg', v: secondH },
        ].map((k) => (
          <div key={k.l} className={cn('rounded-2xl border p-4', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200')}>
            <p className={cn('text-[11px] font-bold uppercase tracking-[0.12em]', isDark ? 'text-slate-500' : 'text-slate-400')}>{k.l}</p>
            <p className={cn('text-2xl font-extrabold tabular-nums mt-1', isDark ? 'text-slate-100' : 'text-slate-800')}>{k.v}</p>
          </div>
        ))}
        <div className={cn('rounded-2xl border p-4', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200')} data-testid="score-trends-period-delta">
          <p className={cn('text-[11px] font-bold uppercase tracking-[0.12em]', isDark ? 'text-slate-500' : 'text-slate-400')}>Period-over-period</p>
          <p className="text-2xl font-extrabold tabular-nums mt-1 inline-flex items-center gap-1" style={{ color: up ? '#10b981' : '#f43f5e' }}>
            {up ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}{delta > 0 ? '+' : ''}{delta}
          </p>
        </div>
      </div>

      <div className={card} data-testid="score-trends-chart">
        <h3 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>LEI score over time</h3>
        <p className={cn('text-xs mb-3', isDark ? 'text-slate-500' : 'text-slate-400')}>Aggregate Learning Effectiveness Index across the portfolio, month over month.</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1f2a44' : '#eef2f7'} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
              <YAxis domain={[Math.max(0, Math.floor(Math.min(...ys) - 6)), Math.min(100, Math.ceil(Math.max(...ys) + 6))]} tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
              <Tooltip content={({ active, payload }) => active && payload?.length ? (
                <div className={cn('rounded-lg border p-2 text-xs', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700')}><b>{payload[0].payload.month}</b>: {payload[0].payload.lei} LEI</div>) : null} />
              <Line type="monotone" dataKey="lei" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <button onClick={() => navigate && navigate('/score-trends?tab=lei')} className={cn('mt-4 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border', isDark ? 'border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10' : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50')} data-testid="score-trends-open-full">
          Open full Score Trends dashboard <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {kpiInfluence.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* LEI → KPI influence bars + driver momentum */}
          <div className={card} data-testid="score-trends-lei-kpi">
            <h3 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>LEI → KPI influence</h3>
            <p className={cn('text-xs mb-3', isDark ? 'text-slate-500' : 'text-slate-400')}>Designed influence (bar) vs observed correlation (black tick) from your KPI actuals. The arrow shows whether that KPI's learning drivers moved up or down — click it to see which measures.</p>
            {alerts.length > 0 && (
              <div className={cn('rounded-xl border p-3 mb-3', isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200')} data-testid="influence-divergence-nudge">
                <p className={cn('text-xs font-bold inline-flex items-center gap-1.5', isDark ? 'text-amber-300' : 'text-amber-800')}>
                  <AlertTriangle className="h-3.5 w-3.5" /> {alerts.length} influence–reality gap{alerts.length > 1 ? 's' : ''} to watch
                </p>
                <ul className={cn('mt-1.5 space-y-1 text-[11px]', isDark ? 'text-amber-200/80' : 'text-amber-800/90')}>
                  {alerts.slice(0, 3).map((a) => (
                    <li key={a.kpi} data-testid={`divergence-alert-${a.kpi}`}>
                      <b>{a.label}</b>: designed to drive strongly ({a.influence}%) but observed correlation is only {a.empirical_r}%{a.sliding ? ' and sliding ▼' : ''} — worth validating the learning is transferring.
                    </li>
                  ))}
                </ul>
                {alerts.length > 3 && <p className={cn('text-[10px] mt-1', isDark ? 'text-amber-300/70' : 'text-amber-700/70')}>+{alerts.length - 3} more below</p>}
              </div>
            )}
            <div className="space-y-2.5">
              {kpiInfluence.map((k) => {
                const mUp = k.momentum >= 0;
                return (
                  <div key={k.kpi} className="flex items-center gap-2.5" data-testid={`lei-kpi-${k.kpi}`}>
                    <span className={cn('w-28 shrink-0 text-xs font-medium', isDark ? 'text-slate-300' : 'text-slate-600')}>{k.label}</span>
                    <div className={cn('flex-1 relative h-3 rounded-full overflow-hidden', isDark ? 'bg-[#1f2a44]' : 'bg-slate-100')}>
                      <div className="h-full rounded-full" style={{ width: `${k.influence}%`, background: 'linear-gradient(90deg,#6366f1,#22d3ee)' }} />
                      {k.empirical_r != null && (
                        <div className={cn('absolute top-[-2px] bottom-[-2px] w-[2px] rounded', isDark ? 'bg-white' : 'bg-slate-900')}
                          style={{ left: `calc(${Math.min(100, k.empirical_r)}% - 1px)` }}
                          title={`Observed correlation: ${k.empirical_r}% (from ${k.empirical_n} measures)`}
                          data-testid={`lei-kpi-observed-${k.kpi}`} />
                      )}
                    </div>
                    <span className={cn('w-9 text-right text-xs font-bold tabular-nums', isDark ? 'text-slate-200' : 'text-slate-700')}>{k.influence}%</span>
                    {k.agreement ? (
                      <span data-testid={`lei-kpi-agreement-${k.kpi}`}
                        className={cn('w-[74px] shrink-0 inline-flex items-center justify-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full',
                          k.agreement === 'agree'
                            ? (isDark ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700')
                            : (isDark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-50 text-amber-700'))}
                        title={k.agreement === 'agree' ? `Model matches reality (observed ${k.empirical_r}%)` : `Designed influence not yet showing in the data (observed ${k.empirical_r}%)`}>
                        {k.agreement === 'agree' ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                        {k.agreement === 'agree' ? 'Agree' : 'Diverge'}
                      </span>
                    ) : <span className="w-[74px] shrink-0 text-right text-[10px] text-slate-400">no data</span>}
                    <button
                      onClick={() => setDrillKpi(k)}
                      className={cn('w-16 shrink-0 inline-flex items-center justify-end gap-0.5 text-[11px] font-semibold tabular-nums rounded hover:opacity-80 transition-opacity')}
                      style={{ color: k.momentum === 0 ? '#94a3b8' : (mUp ? '#10b981' : '#f43f5e') }}
                      title="Click to see which learning measures drove this"
                      data-testid={`lei-kpi-momentum-${k.kpi}`}
                    >
                      {k.momentum === 0 ? '—' : (<>{mUp ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}{mUp ? '+' : ''}{k.momentum}</>)}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className={cn('flex items-center gap-3 text-[10px] mt-3', isDark ? 'text-slate-600' : 'text-slate-400')}>
              <span className="inline-flex items-center gap-1"><span className="w-4 h-2 rounded-full inline-block" style={{ background: 'linear-gradient(90deg,#6366f1,#22d3ee)' }} />Designed influence</span>
              <span className="inline-flex items-center gap-1"><span className={cn('w-[2px] h-3 inline-block', isDark ? 'bg-white' : 'bg-slate-900')} />Observed correlation</span>
              <span>· arrow = driver momentum (click to drill in)</span>
            </div>
          </div>

          {/* AI-generated cached narrative — to the RIGHT of the influence bars */}
          <div className={cn('rounded-2xl border p-5', isDark ? 'bg-gradient-to-br from-[#111a30] to-[#0d1526] border-indigo-500/30' : 'bg-gradient-to-br from-indigo-50/70 to-white border-indigo-200')} data-testid="influence-ai-summary">
            <div className="flex items-center justify-between mb-1.5">
              <h3 className={cn('text-sm font-bold inline-flex items-center gap-1.5', isDark ? 'text-slate-100' : 'text-slate-800')}>
                <Sparkles className="h-4 w-4 text-indigo-500" /> What these relationships mean
              </h3>
              {insight?.insight && (
                <button onClick={() => generateInsight(true)} disabled={genLoading} className={cn('inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg border', isDark ? 'border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10' : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50', genLoading && 'opacity-60')} data-testid="influence-ai-refresh">
                  <RefreshCw className={cn('h-3 w-3', genLoading && 'animate-spin')} /> Refresh
                </button>
              )}
            </div>
            {insight?.insight ? (
              <>
                <p className={cn('text-sm leading-relaxed whitespace-pre-line', isDark ? 'text-slate-300' : 'text-slate-600')} data-testid="influence-ai-text">{insight.insight}</p>
                <div className="flex items-center justify-between mt-3 gap-2">
                  <p className={cn('text-[10px]', isDark ? 'text-slate-600' : 'text-slate-400')}>
                    AI-generated (GPT-4o), cached{insight.generated_at ? ` · ${new Date(insight.generated_at).toLocaleDateString()}` : ''}.
                  </p>
                  <button onClick={toggleHistory} data-testid="influence-ai-history-toggle"
                    className={cn('inline-flex items-center gap-1 text-[11px] font-semibold shrink-0', isDark ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-700 hover:text-indigo-800')}>
                    <History className="h-3 w-3" /> History
                    <ChevronDown className={cn('h-3 w-3 transition-transform', showHistory && 'rotate-180')} />
                  </button>
                </div>
                {showHistory && (
                  <div className={cn('mt-2 border-t pt-2 space-y-2 max-h-56 overflow-y-auto', isDark ? 'border-[#22304d]' : 'border-slate-200')} data-testid="influence-ai-history">
                    {history == null ? (
                      <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
                    ) : (() => {
                      const past = (history || []).filter((h) => h.insight !== insight.insight);
                      return past.length === 0 ? (
                        <p className={cn('text-[11px] py-1', isDark ? 'text-slate-500' : 'text-slate-400')}>No earlier summaries yet — refresh over time to build a quarter-over-quarter story.</p>
                      ) : past.map((h, i) => (
                        <div key={i} className={cn('rounded-lg p-2 text-[11px]', isDark ? 'bg-[#0b1220]' : 'bg-white/70')} data-testid={`influence-ai-history-item-${i}`}>
                          <p className={cn('font-semibold mb-0.5', isDark ? 'text-slate-400' : 'text-slate-500')}>{h.generated_at ? new Date(h.generated_at).toLocaleString() : '—'}{h.strongest ? ` · ↑ ${h.strongest} · ↓ ${h.weakest}` : ''}</p>
                          <p className={cn('leading-snug line-clamp-3', isDark ? 'text-slate-400' : 'text-slate-600')}>{h.insight}</p>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-start gap-3">
                <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Generate a plain-English read of what's driving the strength (and weakness) of these LEI → KPI relationships.</p>
                <button onClick={() => generateInsight(false)} disabled={genLoading} className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white', genLoading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700')} data-testid="influence-ai-generate">
                  {genLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {genLoading ? 'Generating…' : 'Generate AI summary'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Momentum drill-in: which learning measures moved this KPI */}
      <Dialog open={!!drillKpi} onOpenChange={(o) => !o && setDrillKpi(null)}>
        <DialogContent className="max-w-lg" data-testid="momentum-drilldown">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              {drillKpi && (drillKpi.momentum >= 0 ? <ArrowUpRight className="h-4 w-4 text-emerald-600" /> : <ArrowDownRight className="h-4 w-4 text-rose-600" />)}
              What moved {drillKpi?.label}'s drivers
            </DialogTitle>
          </DialogHeader>
          {drillKpi && (
            <div>
              <p className="text-xs text-slate-500 mb-3">
                Net driver momentum <span className="font-bold" style={{ color: drillKpi.momentum >= 0 ? '#10b981' : '#f43f5e' }}>{drillKpi.momentum >= 0 ? '+' : ''}{drillKpi.momentum}</span> this period —
                the learning measures behind {drillKpi.label}, ranked by how much each pushed it up or down (weight-share × recent change).
              </p>
              <div className="space-y-1.5 max-h-[52vh] overflow-y-auto pr-1">
                {(drillKpi.drivers || []).map((d) => {
                  const up = d.contribution >= 0;
                  const mag = Math.min(100, Math.abs(d.contribution) * 40);
                  return (
                    <button key={d.measure_id} type="button"
                      onClick={() => { setDrillKpi(null); navigate && navigate('/configure', { state: { focusMeasureId: d.measure_id, focusMeasureName: d.name, focusKpiId: drillKpi.kpi, focusKpiLabel: drillKpi.label, focusReason: 'momentum-drill' } }); }}
                      className="w-full flex items-center gap-2 text-xs rounded-lg px-1 py-1 hover:bg-slate-100 transition-colors text-left group" data-testid={`drill-measure-${d.measure_id}`}
                      title={`Open ${d.name} in the Single-Measure ROI calculator`}>
                      <span className="w-52 shrink-0 truncate text-slate-700 group-hover:text-indigo-700" title={d.name}>{d.name}</span>
                      <span className="w-14 shrink-0 text-[10px] text-slate-400">{d.pillar?.split(' ')[0]}</span>
                      <div className="flex-1 h-2 rounded-full bg-slate-100 relative overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${mag}%`, background: up ? '#10b981' : '#f43f5e' }} />
                      </div>
                      <span className="w-12 text-right text-[11px] text-slate-400 tabular-nums" title="Recent change in this measure">{d.recent_change >= 0 ? '+' : ''}{d.recent_change}</span>
                      <span className="w-14 text-right text-[11px] font-bold tabular-nums" style={{ color: up ? '#059669' : '#e11d48' }}>{up ? '+' : ''}{d.contribution}</span>
                      <ArrowRight className="h-3 w-3 shrink-0 text-slate-300 group-hover:text-indigo-500" />
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 mt-3">Columns: measure · pillar · relative impact · its own recent change · contribution to this KPI's momentum. Click any measure to open it in the Single-Measure ROI calculator.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
